// Groups synced products into styles (one parent, many colour/size variants) for
// the Fashion layout. Built in memory from db.products.toArray() — no new DB index,
// so the Dexie and SQLite schemas stay unchanged.

const SIZE_ORDER = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "XXXL", "3XL", "4XL", "5XL", "FREE", "FS", "OS"];

const COLOUR_RE = /colou?r|shade/i;
const SIZE_RE = /size/i;

function attrLabel(a) {
  return `${a.group || ""} ${a.name || ""}`;
}

// Picks the colour and size values for one variant. Falls back to the order of
// variant attributes when the master data uses unrecognised names.
export function readVariantAxes(product) {
  const attrs = Array.isArray(product?.variantAttributes) ? product.variantAttributes : [];
  const variantAttrs = attrs.filter((a) => a.isVariant);
  const pool = variantAttrs.length > 0 ? variantAttrs : attrs;
  const colour = pool.find((a) => COLOUR_RE.test(attrLabel(a)));
  const size = pool.find((a) => SIZE_RE.test(attrLabel(a)));
  const rest = variantAttrs.filter((a) => a !== colour && a !== size);
  return {
    colour: colour?.value ?? (!colour && rest.length > 1 ? rest[0].value : null),
    size: size?.value ?? (!size && rest.length > 0 ? rest[rest.length - 1].value : null),
  };
}

export function compareSizes(a, b) {
  const ua = String(a).toUpperCase().trim();
  const ub = String(b).toUpperCase().trim();
  const ia = SIZE_ORDER.indexOf(ua);
  const ib = SIZE_ORDER.indexOf(ub);
  if (ia !== -1 && ib !== -1) return ia - ib;
  const na = parseFloat(ua);
  const nb = parseFloat(ub);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return ua.localeCompare(ub);
}

// Shared leading words of the variant names ("Linen Shirt Navy S", "Linen Shirt
// Navy M" → "Linen Shirt Navy"). Never cuts inside a word, so "Shirt Sand" and
// "Shirt Sage" give "Shirt", not "Shirt Sa".
// The shared start of the variants' item codes ("SKU-A1", "SKU-A2" → "SKU-A"),
// or the first code when they share too little to name the style.
export function styleCode(codes) {
  const first = codes.find(Boolean) || "";
  let prefix = first;
  for (const c of codes) {
    let i = 0;
    while (i < prefix.length && i < c.length && prefix[i] === c[i]) i += 1;
    prefix = prefix.slice(0, i);
  }
  prefix = prefix.replace(/[\s\-_./]+$/, "");
  return prefix.length >= 3 ? prefix : first;
}

export function commonPrefix(names) {
  if (names.length === 0) return "";
  let prefix = names[0];
  for (const n of names.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < n.length && prefix[i] === n[i]) i += 1;
    prefix = prefix.slice(0, i);
  }
  const atBoundary = names.every((n) => n.length === prefix.length || /[\s\-–·/|,(]/.test(n[prefix.length]));
  if (!atBoundary) {
    const lastBreak = Math.max(...[" ", "-", "–", "·", "/", "|", ",", "("].map((c) => prefix.lastIndexOf(c)));
    prefix = lastBreak > 0 ? prefix.slice(0, lastBreak) : "";
  }
  return prefix.replace(/[\s\-–·/|,(]+$/, "").trim();
}

// Returns Map<styleId, Style>. Products without a parentProductId form a
// single-variant style of their own, so every product is reachable.
export function buildStyleIndex(products = []) {
  const groups = new Map();
  for (const p of products) {
    if (!p || p.isactive === "N") continue;
    const styleId = p.parentProductId || p.mProductId;
    if (!styleId) continue;
    if (!groups.has(styleId)) groups.set(styleId, []);
    groups.get(styleId).push(p);
  }

  const index = new Map();
  for (const [styleId, members] of groups) {
    const variants = members.map((p) => ({ product: p, ...readVariantAxes(p) }));
    const colours = [...new Set(variants.map((v) => v.colour).filter((c) => c != null))];
    const sizes = [...new Set(variants.map((v) => v.size).filter((s) => s != null))].sort(compareSizes);
    const names = members.map((p) => p.name || "");
    const name = members.length > 1 ? commonPrefix(names) || names[0] : names[0];
    const prices = members.map((p) => Number(p.sunitprice) || 0);
    const withImage = members.find((p) => p.imageurl || p.imageUrl);
    const parent = members.find((p) => p.mProductId === styleId);
    const codes = members.map((p) => String(p.value || ""));
    const code = parent?.value || styleCode(codes);
    index.set(styleId, {
      styleId,
      name,
      code,
      imageUrl: withImage ? withImage.imageurl || withImage.imageUrl : "",
      brandName: members[0].productBrandName || "",
      categoryId: members[0].mProductCategoryId || "",
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      colours,
      sizes,
      variants,
    });
  }
  return index;
}

// Lookup from any variant's mProductId to its style.
export function styleIdFor(product) {
  return product?.parentProductId || product?.mProductId || null;
}

// The variant at a colour × size cell, or null when that combination is not made.
export function findVariant(style, colour, size) {
  if (!style) return null;
  return (
    style.variants.find((v) => (colour == null || v.colour === colour) && (size == null || v.size === size))?.product ||
    null
  );
}
